import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, AlertCircle, Lock, Eye, EyeOff } from "lucide-react";
import {
  validatePasswordSetup,
  completePasswordSetup
} from "../../services/authService";

const REASON_MESSAGES = {
  missing: "This link is missing its token. Please use the link from your email.",
  invalid: "This link is invalid. Ask your administrator to resend your invite.",
  expired: "This link has expired. Ask your administrator to resend your invite.",
  used: "This link has already been used. If you've set your password, just sign in.",
  inactive: "This account isn't active. Please contact your administrator."
};

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [info, setInfo] = useState(null); // { valid, email, first_name, reason }
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) {
        if (active) {
          setInfo({ valid: false, reason: "missing" });
          setChecking(false);
        }
        return;
      }
      const res = await validatePasswordSetup(token);
      if (!active) return;
      setInfo(res.success ? res : { valid: false, reason: "invalid" });
      setChecking(false);
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    const res = await completePasswordSetup(token, password);
    setSubmitting(false);
    if (res.success) {
      navigate("/dashboard");
    } else {
      setError(res.message || "We couldn't set your password. Please try again.");
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #FCFCFC 0%, #FCFCFC 50%, rgba(0, 111, 234, 0.54) 100%)"
      }}
    >
      <div className="absolute bottom-0 left-0 right-0 h-[50%] bg-gradient-to-t from-blue-100/40 to-transparent" />

      <div className="flex-1 flex flex-col items-center justify-center px-4 relative z-10">
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.svg" alt="Orbit Logo" className="w-10 h-10" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Nativeorange Orbit</h1>
            <p className="text-gray-500 text-sm">
              Integration Core for Agentic Insurance
            </p>
          </div>
        </div>

        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
          {checking ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-600">
              <Loader2 className="w-6 h-6 animate-spin mb-3" />
              <p className="text-sm">Checking your link…</p>
            </div>
          ) : info && info.valid ? (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Set your password
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  {info.first_name ? `Hi ${info.first_name}, ` : ""}create a password
                  for{" "}
                  <span className="font-medium text-gray-800">{info.email}</span> to
                  finish setting up your Orbit account.
                </p>
              </div>

              {error && (
                <div
                  className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
                  role="alert"
                >
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                    <p className="ml-3 text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Re-enter your password"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white text-sm"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Setting password…
                    </span>
                  ) : (
                    "Set password & sign in"
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                This link can't be used
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                {REASON_MESSAGES[info?.reason] || REASON_MESSAGES.invalid}
              </p>
              <Link
                to="/login"
                className="inline-block px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Go to sign in
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="py-6 text-center relative z-10">
        <p className="text-sm text-gray-500">© Orbit · Native Orange</p>
      </div>
    </div>
  );
}
